import { CalendarX, ShieldOff, SearchX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Logo } from '@/shared/ui/logo';

/**
 * The link does not work, and the dentist is told which kind of "does not work".
 *
 * The server distinguishes expired, withdrawn and never-existed, and that
 * distinction is passed through rather than flattened into one message -
 * because what the person should do next differs in each case. Collapsing them
 * into "invalid link" would leave somebody re-checking an address they pasted
 * correctly.
 *
 * Nothing is leaked by telling them: possession of the token is the only
 * secret, and whoever is reading this page already has it.
 */
const VARIANTS = {
  share_link_expired: {
    icon: CalendarX,
    title: 'This link has expired',
    body: 'Review links are set to expire so they cannot be opened indefinitely from an old email. Ask the lab to send a new one - it takes them a moment.',
  },
  share_link_revoked: {
    icon: ShieldOff,
    title: 'This link has been withdrawn',
    body: 'The lab withdrew it, usually because the design changed or it was sent to the wrong address. Ask them for the current link.',
  },
  not_found: {
    icon: SearchX,
    title: 'We could not find this design',
    body: 'The link may have been cut short when it was copied - they are long, and email clients sometimes wrap them. Try clicking the link in the email itself rather than pasting it.',
  },
} as const;

export function LinkUnavailable({ code }: { code: string }) {
  const variant =
    code === 'share_link_expired'
      ? VARIANTS.share_link_expired
      : code === 'share_link_revoked'
        ? VARIANTS.share_link_revoked
        : VARIANTS.not_found;

  const Icon = variant.icon;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center">
            <Logo />
          </div>
          <div className="flex justify-center">
            <span
              className="flex size-10 items-center justify-center rounded-full bg-muted"
              aria-hidden="true"
            >
              <Icon className="size-4 text-muted-foreground" />
            </span>
          </div>
          <CardTitle>{variant.title}</CardTitle>
          <CardDescription className="leading-relaxed">{variant.body}</CardDescription>
        </CardHeader>
        <CardContent className="text-center text-xs text-muted-foreground">
          Nothing you did caused this, and nothing has been lost - the design is still with the lab.
        </CardContent>
      </Card>
    </div>
  );
}
